import { ze } from 'utils/zhEn';

/**
 * Android 崩溃详情「可用 / 总」展示。
 * 对照 crashsight-android dev/v4.3.14 DeviceInfo：
 * - ROM getRomAvailSize / getRomTotalSize：失败保持 -1，成功为块数乘块大小（可为 0）。两次 StatFs 分开算，所以总量 0、可用为正数能同时出现。没有 -2。
 * - SD getFreeSdCard / getTotalSdCard：未挂载返回 0；已挂载后各自是块数乘块大小（可为 0）或抛错 -2。两次调用独立，所以 0 与 -2 可以搭配。getTotalSdCard 不返回 -1。
 * - Native !isHappenNow 把可用空间写成 -1，总空间在这之前已经赋值。
 * source=absent 的组合现行 SDK 没有稳定路径，文案标成「源码无此组合」，不套用单子里那句说明。
 */

const COPY = {
  rom: {
    bothUncollected: ['未采集到存储信息', 'Storage info not collected'],
    availUncollected: ['未采集到存储可用空间', 'Free storage not collected'],
    totalUncollected: ['未采集到存储总空间', 'Total storage not collected'],
    availZeroTotalUncollected: ['存储可用空间为 0，总空间未采集', 'Free storage is 0, total storage not collected'],
    availZero: ['存储可用空间为 0', 'Free storage is 0'],
    bothZero: ['存储可用空间为 0，总空间为 0', 'Free storage is 0 and total storage is 0'],
    totalZero: ['存储总空间为 0', 'Total storage is 0'],
    fallback: ['未采集到存储信息', 'Storage info not collected'],
    absent: ['源码无此组合', 'Combination not produced by SDK'],
  },
  sd: {
    bothZero: ['外部存储未挂载或状态不可用', 'External storage not mounted or unavailable'],
    availZero: ['外部存储可用空间为 0', 'Free external storage is 0'],
    bothFail: ['外部存储已挂载但获取失败', 'External storage mounted but retrieval failed'],
    availFail: ['外部存储可用空间获取失败', 'Failed to get free external storage'],
    totalFail: ['外部存储总空间获取失败', 'Failed to get total external storage'],
    totalZero: ['外部存储总空间为 0', 'Total external storage is 0'],
    availZeroTotalFail: ['外部存储可用空间为 0，总空间获取失败', 'Free external storage is 0, failed to get total'],
    availFailTotalZero: ['外部存储可用空间获取失败，总空间为 0', 'Failed to get free external storage, total is 0'],
    availUncollected: ['未采集到外部存储可用空间', 'Free external storage not collected'],
    fallback: ['未采集到外部存储信息', 'External storage info not collected'],
    absent: ['源码无此组合', 'Combination not produced by SDK'],
  },
};

// 只登记源码里真有的桶。没登记的数值对按 absent 处理。
const EXISTS = {
  rom: {
    'p:p': 'ok',
    'z:p': 'availZero',
    'z:z': 'bothZero',
    'p:z': 'totalZero',
    'm1:m1': 'bothUncollected',
    'm1:p': 'availUncollected',
    'm1:z': 'availUncollected',
    'p:m1': 'totalUncollected',
    'z:m1': 'availZeroTotalUncollected',
  },
  sd: {
    'p:p': 'ok',
    'z:z': 'bothZero',
    'z:p': 'availZero',
    'p:z': 'totalZero',
    'z:m2': 'availZeroTotalFail',
    'm2:z': 'availFailTotalZero',
    'm2:m2': 'bothFail',
    'm2:p': 'availFail',
    'p:m2': 'totalFail',
    'm1:p': 'availUncollected',
    'm1:z': 'availUncollected',
    'm1:m2': 'availUncollected',
  },
};

function isMissing(value) {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  return Number.isNaN(Number(value));
}

function bucket(value) {
  if (value === -1) {
    return 'm1';
  }
  if (value === -2) {
    return 'm2';
  }
  if (value === 0) {
    return 'z';
  }
  if (value > 0) {
    return 'p';
  }
  return 'x';
}

function withTotal(pair, totalText) {
  return [`${pair[0]}（总空间 ${totalText}）`, `${pair[1]} (total ${totalText})`];
}

function withFree(pair, freeText) {
  return [`${pair[0]}（可用空间 ${freeText}）`, `${pair[1]} (free ${freeText})`];
}

export function formatStoragePair(avail, total, options = {}) {
  const namespace = options.namespace === 'sd' ? 'sd' : 'rom';
  const formatCapacity = typeof options.formatCapacity === 'function'
    ? options.formatCapacity
    : (bytes) => String(bytes);
  const table = COPY[namespace];

  if (isMissing(avail) || isMissing(total)) {
    return {
      namespace,
      kind: 'fallback',
      source: 'fallback',
      showBar: false,
      text: ze(table.fallback[0], table.fallback[1]),
    };
  }

  const availNum = Number(avail);
  const totalNum = Number(total);
  const key = `${bucket(availNum)}:${bucket(totalNum)}`;
  const kind = (EXISTS[namespace] || {})[key];

  if (!kind) {
    return {
      namespace,
      kind: 'absent',
      source: 'absent',
      showBar: false,
      text: ze(table.absent[0], table.absent[1]),
    };
  }

  if (kind === 'ok') {
    return {
      namespace,
      kind,
      source: 'exists',
      showBar: true,
      text: `${formatCapacity(availNum)} ${ze('可用', 'Free')} / ${formatCapacity(totalNum)}`,
    };
  }

  let copy = table[kind] || table.fallback;
  if ((kind === 'availZero' || kind === 'availFail') && totalNum > 0) {
    copy = withTotal(copy, formatCapacity(totalNum));
  }
  if (kind === 'totalZero' && availNum > 0) {
    copy = withFree(copy, formatCapacity(availNum));
  }

  return {
    namespace,
    kind,
    source: 'exists',
    showBar: false,
    text: ze(copy[0], copy[1]),
  };
}
